import React, { useState } from 'react';
import { Channel } from '@meradio/shared-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ChannelSearch from './components/ChannelSearch';
import ChannelList from './components/ChannelList';

const Channels: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'search' | 'followed'>(
    searchParams.get('tab') === 'search' ? 'search' : 'followed'
  );

  const handleChannelSelect = (channel: Channel) => {
    navigate(`/channels/${channel.channelId}`);
  };

  return (
    <div className="min-h-screen bg-meradio-cream dark:bg-bg-dark-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary mb-2">
            Channel Management
          </h1>
          <p className="text-meradio-dark-light dark:text-text-dark-secondary text-lg">
            Discover and follow your favorite YouTube music channels
          </p>
        </div>

        <div className="mb-8">
          <div className="border-b border-meradio-light dark:border-border-dark dark:border-border-dark">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('followed')}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                  ${activeTab === 'followed'
                    ? 'border-meradio-primary text-meradio-primary'
                    : 'border-transparent text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-dark dark:text-text-dark-primary hover:border-meradio-light'
                  }
                `}
              >
                Followed Channels
              </button>
              <button
                onClick={() => setActiveTab('search')}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                  ${activeTab === 'search'
                    ? 'border-meradio-primary text-meradio-primary'
                    : 'border-transparent text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-dark dark:text-text-dark-primary hover:border-meradio-light'
                  }
                `}
              >
                Search Channels
              </button>
            </nav>
          </div>
        </div>

        <div className="min-h-[600px]">
          {activeTab === 'followed' && (
            <div className="fade-in">
              <ChannelList onChannelSelect={handleChannelSelect} />
            </div>
          )}

          {activeTab === 'search' && (
            <div className="fade-in">
              <div className="max-w-4xl mx-auto">
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-meradio-dark dark:text-text-dark-primary mb-2">
                    Discover New Channels
                  </h2>
                  <p className="text-meradio-dark-light dark:text-text-dark-secondary">
                    Search for YouTube music channels to follow and automatically import their latest videos
                  </p>
                </div>
                <ChannelSearch 
                  onChannelSelect={handleChannelSelect}
                  maxResults={25}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 pt-8 border-t border-meradio-light dark:border-border-dark dark:border-border-dark">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-white dark:bg-bg-dark-secondary rounded-xl p-6 shadow-sm">
              <div className="text-2xl font-bold text-meradio-primary mb-2">
                🎵
              </div>
              <h3 className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary mb-1">
                Music Discovery
              </h3>
              <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary">
                Automatically import latest tracks from followed channels
              </p>
            </div>
            
            <div className="bg-white dark:bg-bg-dark-secondary rounded-xl p-6 shadow-sm">
              <div className="text-2xl font-bold text-meradio-secondary mb-2">
                🎧
              </div>
              <h3 className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary mb-1">
                Audio Features
              </h3>
              <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary">
                BPM, key detection, and mood analysis for DJ mixing
              </p>
            </div>
            
            <div className="bg-white dark:bg-bg-dark-secondary rounded-xl p-6 shadow-sm">
              <div className="text-2xl font-bold text-meradio-success mb-2">
                ⚡
              </div>
              <h3 className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary mb-1">
                Real-time Sync
              </h3>
              <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary">
                Hourly background imports keep your library up to date
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Channels;
